import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  CalendarDays, Coins, CreditCard, Smartphone, Truck, ChevronRight, Loader2, RefreshCw,
  Camera, AlertCircle, Check,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useClient } from '../context/ClientContext';
import { Button } from '../components/ui/button';
import { SendDeliverable } from '../components/SendDeliverable';
import { ScanSubmissionsStrip } from '../components/ScanSubmissionsStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { key: 'pos_cash',   label: 'Cash',    Icon: Coins,      hue: '#2F5D48' },
  { key: 'pos_card',   label: 'Card',    Icon: CreditCard, hue: '#B06A2C' },
  { key: 'pos_upi',    label: 'UPI',     Icon: Smartphone, hue: '#6E5334' },
  { key: 'agg_swiggy', label: 'Swiggy',  Icon: Truck,      hue: '#B06A2C' },
  { key: 'agg_zomato', label: 'Zomato',  Icon: Truck,      hue: '#8B4B24' },
];

const STATE_INK = {
  matched:    { ink: '#2F5D48', label: 'Matched' },
  short:      { ink: '#B06A2C', label: 'Short' },
  over:       { ink: '#6E5334', label: 'Over' },
  pending:    { ink: '#8B8377', label: 'Pending' },
  unverified: { ink: '#8B8377', label: 'Unverified' },
  missing:    { ink: '#6b3a2c', label: 'Missing' },
};

const fmtRs = (v, allowNull = false) => {
  if (v == null && allowNull) return '—';
  return `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const yesterdayIso = () => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const StatePill = ({ state }) => {
  const meta = STATE_INK[state] || { ink: '#6E5334', label: state };
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{ background: meta.ink, color: '#F4F0E6' }}
      data-testid={`state-pill-${state}`}
    >{meta.label}</span>
  );
};

const ChannelTile = ({ channel, verdict, active, onClick }) => {
  const { key, label, Icon, hue } = channel;
  const state = verdict?.state || 'unverified';
  const meta = STATE_INK[state] || {};
  return (
    <button
      onClick={onClick}
      className={`text-left bg-white border rounded-md p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        active ? 'border-[#2F5D48] ring-2 ring-[#2F5D48]/20' : 'border-[#EDE5D3]'
      }`}
      style={{ borderTopColor: hue, borderTopWidth: 3 }}
      data-testid={`tile-${key}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: hue }} />
          <span className="font-semibold text-[#211C16]">{label}</span>
        </div>
        <StatePill state={state} />
      </div>
      <div className="space-y-1 text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
        <div className="flex justify-between text-xs text-text-muted">
          <span>Expected</span><span className="font-mono">{fmtRs(verdict?.expected)}</span>
        </div>
        <div className="flex justify-between text-xs text-text-muted">
          <span>Actual</span><span className="font-mono">{fmtRs(verdict?.actual, true)}</span>
        </div>
        <div className="flex justify-between text-sm pt-1 border-t border-[#F4F0E6]">
          <span className="text-text-secondary">Variance</span>
          <span className="font-mono font-semibold" style={{ color: (verdict?.variance || 0) < 0 ? '#B06A2C' : meta.ink }}>
            {verdict?.variance == null ? '—'
              : `${verdict.variance >= 0 ? '+' : ''}${fmtRs(verdict.variance)}`}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-end mt-3 text-[11px] text-[#6E5334]">
        Drill <ChevronRight className="h-3 w-3 ml-0.5" />
      </div>
    </button>
  );
};

const KIND_LABEL = {
  commission_drift:     'Commission drift',
  refund:               'Refund clawback',
  underpaid:            'Underpaid',
  pending_utr:          'Pending bank credit',
  mdr_overcharge:       'MDR overcharge',
  mdr_undercharge:      'MDR undercharge',
  missing_bank_credit:  'Missing bank credit',
  pending_settlement:   'Pending settlement',
  unmatched_credit:     'Unmatched bank credit',
  cash_short:           'Cash short',
  cash_over:            'Cash over',
};

const VarianceExplanation = ({ explanation, channelLabel }) => {
  if (!explanation) return null;
  const { state, variance_amount, headline, offenders = [] } = explanation;
  const impactInk = variance_amount < 0 ? '#B06A2C' : variance_amount > 0 ? '#2F5D48' : '#6E5334';
  const impactPrefix = variance_amount > 0 ? '+' : '';
  return (
    <div className="mb-5 rounded-md border border-[#EDE5D3] bg-[#FBF8F0]" data-testid="variance-explanation">
      <div className="px-4 py-3 border-b border-[#EDE5D3] flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[.14em] text-[#6E5334]">
            {channelLabel} · what caused the variance
          </div>
          <div className="text-sm text-[#211C16] mt-1 max-w-2xl">{headline}</div>
        </div>
        {variance_amount !== 0 && (
          <div className="text-right shrink-0">
            <div className="text-[10px] tracking-[.14em] uppercase text-[#6E5334]">Tile variance</div>
            <div className="font-semibold" style={{ color: impactInk, fontFamily: 'Fraunces, serif', fontSize: 20 }}>
              {impactPrefix}₹{Math.abs(variance_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          </div>
        )}
      </div>
      {offenders.length > 0 && (
        <div className="divide-y divide-[#EDE5D3]">
          {offenders.slice(0, 10).map((o, i) => (
            <div key={i} className="px-4 py-3 flex items-start justify-between gap-4" data-testid={`offender-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold"
                        style={{ background: '#F4E9D3', color: '#8B4B24' }}>
                    {KIND_LABEL[o.kind] || o.kind.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs font-mono text-[#211C16] truncate" title={o.id}>{o.id}</span>
                </div>
                <div className="text-[13px] text-[#211C16]">{o.reason}</div>
                {o.evidence && Object.keys(o.evidence).length > 0 && (
                  <details className="mt-1">
                    <summary className="text-[11px] text-[#6E5334] cursor-pointer hover:text-[#B06A2C]">
                      Evidence · {Object.keys(o.evidence).length} field{Object.keys(o.evidence).length === 1 ? '' : 's'}
                    </summary>
                    <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] font-mono text-text-secondary">
                      {Object.entries(o.evidence).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <span className="text-[#6E5334]">{k}:</span>
                          <span className="truncate">{v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-[.12em] text-[#6E5334]">Impact</div>
                <div className="font-semibold text-sm" style={{ color: o.amount < 0 ? '#B06A2C' : o.amount > 0 ? '#2F5D48' : '#6E5334' }}>
                  {o.amount > 0 ? '+' : ''}₹{Math.abs(o.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          ))}
          {offenders.length > 10 && (
            <div className="px-4 py-2 text-[11px] text-[#6E5334] italic bg-[#FBF8F0]">
              +{offenders.length - 10} more offender{offenders.length - 10 === 1 ? '' : 's'} — scroll the raw rows below to see them all.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DrillPanel = ({ clientId, date, channel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientId || !date || !channel) return;
    let cancelled = false;
    setLoading(true); setError('');
    axios.get(`${API}/daily-recon/${clientId}/drill`, {
      params: { business_date: date, channel },
    }).then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load drill-down'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, date, channel]);

  if (loading) return (
    <div className="p-6 text-sm text-text-muted flex items-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading evidence…
    </div>
  );
  if (error) return <div className="p-6 text-sm text-state-error">{error}</div>;
  if (!data) return null;

  const meta = CHANNELS.find((c) => c.key === channel) || {};

  return (
    <div className="bg-white border border-[#EDE5D3] rounded-md p-5" data-testid={`drill-${channel}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {meta.Icon && <meta.Icon className="h-4 w-4" style={{ color: meta.hue }} />}
          <h3 className="font-semibold text-[#211C16]">Evidence for {meta.label} · {date}</h3>
        </div>
        {data.verdict?.reason && (
          <span className="text-xs italic text-[#6E5334]">Reason: {data.verdict.reason}</span>
        )}
      </div>

      <VarianceExplanation
        explanation={data.variance_explanation}
        channelLabel={meta.label}
      />

      <div className="text-[10px] uppercase tracking-[.12em] text-[#6E5334] mb-2">
        Raw source rows
      </div>

      {Object.entries(data.sources || {}).map(([coll, rows]) => (
        <div key={coll} className="mb-4">
          <div className="text-[10px] uppercase tracking-[.12em] text-[#6E5334] mb-1.5">
            {coll.replace(/_/g, ' ')} · <span className="font-normal">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
          </div>
          {rows.length === 0 ? (
            <div className="text-xs text-text-muted italic py-2 px-2 border border-dashed border-[#EDE5D3] rounded">
              No rows on file for this day.
            </div>
          ) : (
            <div className="border border-[#EDE5D3] rounded overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#FBF8F0] sticky top-0">
                  <tr className="text-left text-[10px] uppercase tracking-[.1em] text-[#6E5334]">
                    {Object.keys(rows[0]).filter((k) => !['_id', 'client_id'].includes(k)).slice(0, 6).map((k) => (
                      <th key={k} className="py-1.5 px-2">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 25).map((row, i) => (
                    <tr key={i} className="border-t border-[#F4F0E6]">
                      {Object.keys(rows[0]).filter((k) => !['_id', 'client_id'].includes(k)).slice(0, 6).map((k) => (
                        <td key={k} className="py-1 px-2 font-mono text-[11px] text-text-secondary truncate max-w-[220px]">
                          {typeof row[k] === 'object' ? JSON.stringify(row[k]) : String(row[k] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 25 && (
                <div className="text-[11px] text-text-muted text-center py-1 border-t border-[#EDE5D3] bg-[#FBF8F0]">
                  +{rows.length - 25} more rows
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const DailyRecon = () => {
  const { selectedClient, clients } = useClient();
  const [searchParams] = useSearchParams();
  const clientFromUrl = searchParams.get('client');
  const dateFromUrl = searchParams.get('date');
  const channelFromUrl = searchParams.get('channel');

  const [date, setDate] = useState(dateFromUrl || yesterdayIso());
  const [endDate, setEndDate] = useState('');   // '' = single-day mode
  const isRange = Boolean(endDate) && endDate !== date;
  const [tiles, setTiles] = useState([]);
  const [activeChannel, setActiveChannel] = useState(channelFromUrl || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Resolve which client we're viewing. If URL says one, prefer it (deep link
  // from Exceptions). Otherwise use the selected client.
  const activeClient = useMemo(() => {
    if (clientFromUrl) return { id: clientFromUrl,
                                  name: clients?.find((c) => c.id === clientFromUrl)?.name || clientFromUrl };
    return selectedClient;
  }, [clientFromUrl, selectedClient, clients]);

  const load = useCallback(async () => {
    if (!activeClient?.id) return;
    // In range mode the /summary endpoint already sums across the window
    // and returns per-tile totals + state_counts. Single-day mode calls
    // it with start=end (unchanged from before).
    const start = date;
    const end = isRange ? endDate : date;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/daily-recon/${activeClient.id}/summary`, {
        params: { start, end },
      });
      setTiles(data.tiles || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [activeClient?.id, date, endDate, isRange]);

  useEffect(() => { load(); }, [load]);

  const runEngine = async () => {
    if (!activeClient?.id) return;
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/daily-recon/${activeClient.id}/reconcile`, null, {
        params: { business_date: date },
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to run engine');
    } finally {
      setLoading(false);
    }
  };

  const verdictByChannel = tiles.reduce((acc, t) => {
    acc[t.channel] = {
      expected: t.expected, actual: t.actual, variance: t.variance,
      state: Object.entries(t.state_counts || {}).find(([, v]) => v > 0)?.[0] || 'unverified',
    };
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="daily-recon-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[.18em] text-[#6E5334] uppercase">Daily reconciliation</div>
          <h1 className="text-3xl font-semibold text-[#211C16] mt-1" style={{ fontFamily: 'Fraunces, serif' }}>
            {activeClient?.name || 'Select a client'}
          </h1>
          <p className="text-sm text-text-secondary mt-2 max-w-2xl">
            Five channels reconciled at day grain. Each tile traces back to the exact bills, batches, UTRs or cash-close entries that produced its number. Click any tile to see the source rows, or set a "to" date to roll the tiles up across a range.
          </p>
          <ScanSubmissionsStrip clientId={activeClient?.id} limit={7} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-sm">
            <CalendarDays className="h-4 w-4 text-[#6E5334]" />
            <input
              type="date"
              value={date}
              max={endDate || todayIso()}
              onChange={(e) => { setDate(e.target.value); setActiveChannel(null); }}
              className="border border-border rounded-md px-2 py-1.5 text-xs"
              data-testid="daily-recon-date"
              title="From"
            />
            <span className="text-[10px] text-[#8B8377] px-1">to</span>
            <input
              type="date"
              value={endDate}
              min={date}
              max={todayIso()}
              onChange={(e) => { setEndDate(e.target.value); setActiveChannel(null); }}
              className="border border-border rounded-md px-2 py-1.5 text-xs"
              data-testid="daily-recon-end-date"
              placeholder="single day"
              title="To (optional — leave empty for a single day)"
            />
            {isRange && (
              <button
                type="button"
                onClick={() => setEndDate('')}
                className="text-[10px] text-[#6E5334] hover:text-[#B06A2C] underline underline-offset-2 ml-1"
                data-testid="daily-recon-clear-range"
              >clear</button>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => { setDate(yesterdayIso()); setEndDate(''); setActiveChannel(null); }} data-testid="daily-recon-yesterday">Yesterday</Button>
          <Button size="sm" onClick={runEngine} disabled={loading || !activeClient?.id || isRange} title={isRange ? 'Run engine works per-day — clear the "to" date to enable' : undefined} data-testid="daily-recon-run">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="ml-1">Run engine</span>
          </Button>
        </div>
      </div>

      {isRange && (
        <div className="mb-4 text-[11px] text-[#6E5334] bg-[#FAF6EC] border border-[#EDE5D3] rounded px-3 py-2" data-testid="daily-recon-range-note">
          Range mode · showing rolled-up totals across <b>{date}</b> to <b>{endDate}</b>. Per-day drill-down and the "Send this deliverable" toolbar are day-level actions and are hidden — pick a single date to see them.
        </div>
      )}

      {activeClient?.id && !isRange && (
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] tracking-[.14em] uppercase text-[#6E5334]">Send today's recon</span>
          <SendDeliverable
            clientId={activeClient.id}
            clientName={activeClient.name}
            businessDate={date}
          />
        </div>
      )}

      {error && (
        <div className="text-sm text-state-error bg-red-50 border border-red-200 rounded p-2 mb-4">
          {error}
        </div>
      )}

      {!activeClient?.id ? (
        <div className="bg-white border border-[#EDE5D3] rounded-md p-6 text-sm text-text-muted italic">
          Pick a client from the top-right dropdown to see the day's reconciliation.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {CHANNELS.map((ch) => (
              <ChannelTile
                key={ch.key}
                channel={ch}
                verdict={verdictByChannel[ch.key]}
                active={activeChannel === ch.key && !isRange}
                onClick={() => !isRange && setActiveChannel(activeChannel === ch.key ? null : ch.key)}
              />
            ))}
          </div>

          {activeChannel && !isRange && (
            <DrillPanel
              clientId={activeClient.id}
              date={date}
              channel={activeChannel}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DailyRecon;
